import { useState } from 'react';

import { Field, FieldProps, FieldRenderProps } from 'react-final-form';

import { MultipleRadioInput as Input } from '@vk-hr-tek/ui/input';

/**
 * Компонент группы радиокнопок для множественного выбора.
 *
 * @public
 * @param disabled - Флаг, указывающий, что компонент отключен.
 * @param label - Метка группы радиокнопок.
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Необязательная всплывающая подсказка для отображения с меткой.
 * @param name - Имя поля формы.
 * @param options - Массив опций, где каждая опция содержит метку и идентификатор.
 * @param trueLabel - Метка для значения 'always'.
 * @param falseLabel - Метка для значения 'direct_only'.
 * @param allTrueLabel - Метка для опции 'all' в групповой радиокнопке.
 * @param allFalseLabel - Метка для опции 'none' в групповой радиокнопке.
 * @param neverLabel - Метка для значения 'never'.
 * @param withoutGroup - Флаг отсутсвтия групповых радиокнопок.
 * @returns Компонент группы радиокнопок.
 */
export const MultipleRadioInput = ({
  disabled = false,
  label,
  labelIcon,
  tooltip = null,
  name,
  options,
  trueLabel,
  falseLabel,
  neverLabel,
  allTrueLabel,
  allFalseLabel,
  withoutGroup,
  ...rest
}: {
  options: { label: string; id: string | number; disabled?: boolean }[];
} & FieldProps<
  { id: string; value: 'always' | 'direct_only' | 'never' }[] | undefined,
  FieldRenderProps<
    { id: string; value: 'always' | 'direct_only' | 'never' }[] | undefined
  >
>) => {
  const [byHand, setByHand] = useState(false);

  return (
    <Field name={name} {...rest}>
      {({ input }) => {
        let groupValue: 'all' | 'none' | 'never' | 'hand' = 'hand';

        const values: {
          id: string | number;
          value: 'always' | 'direct_only' | 'never';
        }[] = options.map((option) => {
          const value =
            (input.value &&
              input.value.find((inputValue) => inputValue.id === option.id)) ||
            null;

          return value || { id: option.id, value: 'always' };
        });

        if (
          values.filter((opt) => opt.value === 'always').length ===
            options.length &&
          !byHand
        ) {
          groupValue = 'all';
        } else if (
          values.filter((opt) => opt.value === 'direct_only').length ===
            options.length &&
          !byHand
        ) {
          groupValue = 'none';
        } else if (
          values.filter((opt) => opt.value === 'never').length ===
            options.length &&
          !byHand
        ) {
          groupValue = 'never';
        }

        const handleChangeForAllElements = (optionValue: string | number) => {
          if (optionValue === 'all') {
            input.onChange(
              options.map((opt) => ({ id: opt.id, value: 'always' })),
            );
            setByHand(false);
          } else if (optionValue === 'none') {
            input.onChange(
              options.map((opt) => ({ id: opt.id, value: 'direct_only' })),
            );
            setByHand(false);
          } else if (optionValue === 'never') {
            input.onChange(
              options.map((opt) => ({ id: opt.id, value: 'never' })),
            );
            setByHand(false);
          } else {
            setByHand(true);
          }
        };

        const handleChangeForElement =
          (id: string | number) => (optionValue: string | number) => {
            const currentValue = values || [];

            if (optionValue === 'always') {
              input.onChange([
                ...currentValue.filter((opt) => opt.id !== id),
                { id, value: 'always' },
              ]);
              setByHand(false);
            } else if (optionValue === 'direct_only') {
              input.onChange([
                ...currentValue.filter((opt) => opt.id !== id),
                { id, value: 'direct_only' },
              ]);
              setByHand(false);
            } else if (optionValue === 'never') {
              input.onChange([
                ...currentValue.filter((opt) => opt.id !== id),
                { id, value: 'never' },
              ]);
              setByHand(false);
            }
          };

        return (
          <Input
            {...input}
            falseLabel={falseLabel}
            neverLabel={neverLabel}
            disabled={disabled}
            groupValue={groupValue}
            label={label}
            labelIcon={labelIcon}
            tooltip={tooltip}
            onChangeForAllElements={handleChangeForAllElements}
            onChangeForElement={handleChangeForElement}
            options={options}
            trueLabel={trueLabel}
            values={values}
            name={name}
            allTrueLabel={allTrueLabel}
            allFalseLabel={allFalseLabel}
            withoutGroup={withoutGroup}
          />
        );
      }}
    </Field>
  );
};
